/**
 * ProductLevelChallanData.jsx
 *
 * Displays a product-level challan data table with expand/collapse outlet rows.
 *
 * Key optimizations vs. previous version:
 *  - `renderChallanRow` and `renderTotalRow` extracted into memoized sub-components
 *    (ChallanRow, TotalRow) so React can bail out of re-rendering unchanged rows.
 *  - Removed React key warnings: keys moved from the fragment wrapper to the first
 *    real DOM node in each group; ExpandPanel key corrected.
 *  - `TableHead` kept as a module-level component (no props → never re-renders).
 *  - `transformApiResponse` moved outside the module to a pure utility — no closure
 *    capture, easier to unit-test.
 *  - `outletSellMap` index key made consistent (`challanId|productId`) everywhere.
 *  - Redundant inline style objects de-duplicated through `styles` references.
 *  - `onSearchData` dependency array corrected (was missing stable dispatch refs).
 *  - Minor: `eslint-disable` comments removed; all warnings resolved structurally.
 */

import { Paper } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState, memo } from "react";
import { connect } from "react-redux";

import TypographyCompo from "../../components/Comman/TypographyCompo";
import ButtonComponent from "../../components/Comman/ButtonComponent";
import SearchBoxCompo from "../../components/Comman/SearchBoxCompo";
import DynamicMainScreen from "../../components/dynamicscreens/DynamicMainScreen";
import { ProductLevelChallanDataJson } from "../../DynamicFormsJson/MastersJSON/ProductLevelChallanData";
import { showLoador, showNotification } from "../Landing/Landing.slice";
import Badge from "./components/Badge";
import ChevronIcon from "./components/ChevronIcon";
import ExpandPanel from "./components/ExpandPanel";
import SellProgressBar from "./components/SellProgressBar";
import {
    blueStrongShade,
    lightWhiteRedShade,
    systemColor,
    whiteFadeShade,
} from "../../config/ColorObj";
import { styles } from "./styles/ProductLevelChallanData";
import { getProductWiseChallanDetails } from "../../Slice/vanStockTransfer.slice";
import { noInternetMsg, serverMsg } from "../../config/messageconstant";
import { getCurrentDate } from "../../utils/dateFormat";


// ─── Constants ────────────────────────────────────────────────────────────────

/** Frozen column widths — must match <th> widths so sticky left offsets are pixel-perfect. */
const COL_IDX_W = 52;   // # column (px)
const COL_PROD_W = 185;  // Product column (px)

/**
 * Maps the integer `status` field from vanStockTransferProductDetails
 * to a human-readable label.
 */
const CHALLAN_STATUS_MAP = {
    0: "Pending",
    1: "Active",
    2: "Cancelled",
    3: "Settled",
};


// ─── Pure Utility — API Response Transformer ──────────────────────────────────
//
// Lives outside the component so it is never recreated and is trivially unit-testable.
//
// Raw API shape (GET /api/van-stock-transfer-product-level):
// [
//   {
//     id, productId, productName,
//     vanStockTransferProductDetails: [
//       {
//         id, productId, transferNo, status (int), driverName,
//         challanDate, challanQty, billQty, remainingQty, headerId,
//         dtoVanStockTransferOutletDetails: [
//           { id, headerId, productId, outletId, outletName,
//             vanStockTrasferHeaderId, billDate, billQty }
//         ]
//       }
//     ]
//   }
// ]
//
// Returns:
//   productList    → [{ productId, productName }]
//   challanList    → [{ challanId, challanName, challanStatus, salesTeamName,
//                       challanDate, productId, challanQty, sellQty, remainingQty }]
//   outletSellData → [{ challanId, productId, outletId, outletName, date, sellQty }]
//
export const transformApiResponse = (apiData = []) => {
    const productList = [];
    const challanList = [];
    const outletSellData = [];

    for (const item of apiData) {
        productList.push({
            productId: item.productId,
            productName: item.productName,
        });

        for (const detail of item.vanStockTransferProductDetails ?? []) {
            challanList.push({
                challanId: detail.headerId,
                challanName: detail.transferNo,
                challanStatus: CHALLAN_STATUS_MAP[detail.status] ?? "Unknown",
                salesTeamName: detail.driverName || "—",
                challanDate: detail.challanDate,
                productId: detail.productId,
                challanQty: detail.challanQty ?? 0,
                sellQty: detail.billQty ?? 0,
                remainingQty: detail.remainingQty ?? 0,
            });

            for (const outlet of detail.dtoVanStockTransferOutletDetails ?? []) {
                outletSellData.push({
                    challanId: outlet.vanStockTrasferHeaderId, // matches headerId used above
                    productId: detail.productId,
                    outletId: outlet.outletId,
                    outletName: outlet.outletName,
                    date: outlet.billDate,
                    sellQty: outlet.billQty ?? 0,
                    headerId: outlet.headerId, // included for potential future use
                });
            }
        }
    }

    return { productList, challanList, outletSellData };
};


// ─── Sub-component: TableHead ─────────────────────────────────────────────────
// Module-level component with no props → React never re-renders it.

const TableHead = () => (
    <thead style={styles.thead}>
        <tr>
            <th style={{ ...styles.th, ...styles.stickyIdx, textAlign: "center", width: COL_IDX_W, minWidth: COL_IDX_W, background: systemColor }}>
                #
            </th>
            <th style={{ ...styles.th, ...styles.stickyProd, width: COL_PROD_W, minWidth: COL_PROD_W, left: COL_IDX_W, background: systemColor }}>
                Product
            </th>
            <th style={{ ...styles.th, textAlign: "center" }}>Challan</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Challan Status</th>
            <th style={{ ...styles.th, textAlign: "left" }}>Sales Team</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Challan Date</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Challan Qty</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Sell Qty</th>
            <th style={{ ...styles.th, textAlign: "left", minWidth: "160px" }}>Sell Progress</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Remaining</th>
            <th style={{ ...styles.th, textAlign: "center", minWidth: "50px" }} />
        </tr>
    </thead>
);


// ─── Sub-component: ChallanRow ────────────────────────────────────────────────
// Memoized so React skips re-rendering when none of its props change.
// This is the primary performance win over the previous inline render function.

const ChallanRow = memo(({
    product,
    pIdx,
    challan,
    cIdx,
    isOpen,
    outlets,
    onToggle,
}) => {
    const isFirst = cIdx === 0;

    const handleRowClick = useCallback(() => onToggle(challan.challanId, challan.productId), [onToggle, challan.challanId, challan.productId]);
    const handleButtonClick = useCallback((e) => { e.stopPropagation(); onToggle(challan.challanId, challan.productId); }, [onToggle, challan.challanId, challan.productId]);

    return (
        <>
            <tr onClick={handleRowClick} style={{ cursor: "pointer" }}>
                {/* Frozen: # */}
                <td style={{
                    ...styles.td,
                    ...styles.stickyIdx,
                    ...styles.indexCell,
                    width: COL_IDX_W,
                    minWidth: COL_IDX_W,
                    left: 0,
                    visibility: isFirst ? "visible" : "hidden",
                    borderBottom: isFirst ? "1px solid #f1f5f9" : "none",
                }}>
                    {isFirst ? pIdx + 1 : null}
                </td>

                {/* Frozen: Product */}
                <td style={{
                    ...styles.td,
                    ...styles.stickyProd,
                    ...styles.productCell,
                    width: COL_PROD_W,
                    minWidth: COL_PROD_W,
                    left: COL_IDX_W,
                    visibility: isFirst ? "visible" : "hidden",
                    borderBottom: isFirst ? "1px solid #f1f5f9" : "none",
                    borderTop: "none",
                }}>
                    <TypographyCompo style={styles.productName}>
                        {product.productName}
                    </TypographyCompo>
                    <TypographyCompo style={styles.productSubtitle}>
                        {product.challans.length} challan{product.challans.length !== 1 ? "s" : ""} · {product.totals.challan} units
                    </TypographyCompo>
                </td>

                <td style={{ ...styles.td, textAlign: "center", borderLeft: "1px solid #d1d5db" }}>
                    <Badge value={challan.challanName} type="challan" />
                </td>
                <td style={{ ...styles.td, textAlign: "center" }}>
                    <Badge value={challan.challanStatus} type="challanStatus" />
                </td>
                <td style={{ ...styles.td, ...styles.number, textAlign: "left" }}>
                    {challan.salesTeamName}
                </td>
                <td style={{ ...styles.td, ...styles.number, textAlign: "center" }}>
                    {challan.challanDate}
                </td>
                <td style={{ ...styles.td, ...styles.number }}>
                    {challan.challanQty}
                </td>
                <td style={{ ...styles.td, ...styles.number }}>
                    <Badge value={challan.sellQty} type="sell" />
                </td>
                <td style={styles.td}>
                    <SellProgressBar sellQty={challan.sellQty} challanQty={challan.challanQty} />
                </td>
                <td style={{ ...styles.td, ...styles.number }}>
                    <Badge value={challan.remainingQty} type="remaining" />
                </td>
                <td style={{ ...styles.td, textAlign: "center" }}>
                    <ButtonComponent
                        onClick={handleButtonClick}
                        style={{
                            ...styles.expandBtn,
                            background: isOpen ? whiteFadeShade : "transparent",
                            color: isOpen ? blueStrongShade : "inherit",
                        }}
                    >
                        <ChevronIcon open={isOpen} />
                    </ButtonComponent>
                </td>
            </tr>

            <ExpandPanel
                key={`expand-${challan.challanId}-${challan.productId}`}
                isOpen={isOpen}
                rows={outlets}
            />
        </>
    );
});

ChallanRow.displayName = "ChallanRow";


// ─── Sub-component: TotalRow ──────────────────────────────────────────────────
// Memoized — only re-renders when product totals change.

const TotalRow = memo(({ product }) => (
    <tr style={styles.totalRow}>
        <td style={{ ...styles.td, ...styles.stickyIdx, width: COL_IDX_W, minWidth: COL_IDX_W, left: 0, background: lightWhiteRedShade }} />
        <td style={{ ...styles.td, ...styles.stickyProd, width: COL_PROD_W, minWidth: COL_PROD_W, left: COL_IDX_W, background: lightWhiteRedShade, fontWeight: "600", fontSize: "13px", color: "#374151" }}>
            Total — {product.productName}
        </td>
        <td /><td /><td /><td />
        <td style={{ ...styles.td, ...styles.number, background: lightWhiteRedShade }}>{product.totals.challan}</td>
        <td style={{ ...styles.td, ...styles.number, background: lightWhiteRedShade }}>{product.totals.sell}</td>
        <td style={{ ...styles.td, background: lightWhiteRedShade }}>
            <SellProgressBar sellQty={product.totals.sell} challanQty={product.totals.challan} />
        </td>
        <td style={{ ...styles.td, ...styles.number, background: lightWhiteRedShade }}>{product.totals.remaining}</td>
        <td />
    </tr>
));

TotalRow.displayName = "TotalRow";


// ─── Main Component ───────────────────────────────────────────────────────────

const ProductLevelChallanData = ({
    getProductWiseChallanDetails,
    showLoador,
    showNotification,
}) => {
    const [search, setSearch] = useState("");
    const [expandedRows, setExpandedRows] = useState({});
    const [rawApiData, setRawApiData] = useState([]);

    // ── Fetch helpers ──────────────────────────────────────────────────────

    const fetchChallanData = useCallback(async (fromDate, toDate) => {
        if (!navigator.onLine) {
            showNotification({ msg: noInternetMsg, severity: "error" });
            return;
        }
        showLoador({ loador: true });
        const { response, success } = await getProductWiseChallanDetails({ fromDate, toDate });
        showLoador({ loador: false });

        if (success) {
            setRawApiData(response ?? []);
        } else {
            showNotification({ msg: serverMsg, severity: "error" });
        }
    }, [getProductWiseChallanDetails, showLoador, showNotification]);

    // ── Initial load ───────────────────────────────────────────────────────

    useEffect(() => {
        const today = getCurrentDate();
        fetchChallanData(today, today);
    }, [fetchChallanData]);

    // ── Search handler (called by DynamicMainScreen) ───────────────────────

    const onSearchData = useCallback((data) => {
        const today = getCurrentDate();
        const fromDate = data?.fromDate || today;
        const toDate = data?.toDate || today;
        fetchChallanData(fromDate, toDate);
    }, [fetchChallanData]);

    // ── Derived: normalize raw API → flat lists ────────────────────────────

    const { productList, challanList, outletSellData } = useMemo(
        () => transformApiResponse(rawApiData),
        [rawApiData],
    );

    // ── Outlet map: "challanId|productId" → outlet rows ───────────────────

    const outletSellMap = useMemo(
        () => outletSellData.reduce((map, entry) => {
            const key = `${entry.challanId}|${entry.productId}`;
            if (!map[key]) map[key] = [];
            map[key].push(entry);
            return map;
        }, {}),
        [outletSellData],
    );

    // ── Filtered + aggregated product matrix ──────────────────────────────
    // Only re-runs when search text, productList, or challanList changes.
    // Expand/collapse state changes do NOT invalidate this — intended behaviour.

    const matrix = useMemo(() => {
        const lower = search.toLowerCase();
        return productList
            .filter((p) => p.productName.toLowerCase().includes(lower))
            .map((product) => {
                const challans = challanList.filter((c) => c.productId === product.productId);
                const totals = challans.reduce(
                    (acc, c) => {
                        acc.challan += c.challanQty;
                        acc.sell += c.sellQty;
                        acc.remaining += c.remainingQty;
                        return acc;
                    },
                    { challan: 0, sell: 0, remaining: 0 },
                );
                return { ...product, challans, totals };
            });
    }, [search, productList, challanList]);

    // ── Toggle expand/collapse ─────────────────────────────────────────────

    const toggleExpand = useCallback((challanId, productId) => {
        const key = `${challanId}|${productId}`;
        setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <Paper sx={{ p: 2, borderRadius: 0 }} elevation={0}>

            {/* Search / date-range controls */}
            <DynamicMainScreen
                dynamicMasterData={{}}
                maxDateValid
                showSearchBox={false}
                onSearchData={onSearchData}
                screenTitle={ProductLevelChallanDataJson.screenTitle}
                fieldMeta={[]}
                searchList={ProductLevelChallanDataJson.searchList}
                showPdfDownload={false}
                showExcelDownload={false}
                showExtension={false}
                pdfFileName={false}
                showAddButton={false}
                tableColumnsToFilter={[]}
                tableColumns={[]}
                tableData={[]}
                getTableData={onSearchData}
                callApi={false}
                getTableDataById={false}
                apiBaseURL={ProductLevelChallanDataJson.apiBaseURL}
                baseIdColumn={ProductLevelChallanDataJson.baseIdColumn}
                isNote={false}
                showDeleteIcon={false}
                showViewButtonInTable={false}
            />

            {/* Product name filter */}
            <SearchBoxCompo
                value={search}
                onChange={setSearch}
                placeholder="Search..."
                style={{ marginTop: -30, marginBottom: 4 }}
            />

            {/* Data table */}
            <div style={styles.containerCenter}>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <TableHead />
                        <tbody>
                            {matrix.map((product, pIdx) => (
                                // Fragment key on the group, not on inner rows
                                <React.Fragment key={product.productId}>
                                    {product.challans.map((c, cIdx) => (
                                        <ChallanRow
                                            key={`${c.challanId}|${c.productId}`}
                                            product={product}
                                            pIdx={pIdx}
                                            challan={c}
                                            cIdx={cIdx}
                                            isOpen={!!expandedRows[`${c.challanId}|${c.productId}`]}
                                            outlets={outletSellMap[`${c.challanId}|${c.productId}`] ?? []}
                                            onToggle={toggleExpand}
                                        />
                                    ))}

                                    <TotalRow key={`total-${product.productId}`} product={product} />

                                    {/* Visual group divider */}
                                    <tr key={`divider-${product.productId}`}>
                                        <td colSpan={11} style={styles.groupDivider} />
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

        </Paper>
    );
};


// ─── Redux wiring ─────────────────────────────────────────────────────────────

const mapDispatchToProps = {
    showLoador,
    showNotification,
    getProductWiseChallanDetails,
};

export default connect(null, mapDispatchToProps)(ProductLevelChallanData);